import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

import { IconClose } from '../../../common/icons';
import ClientFarmSearch from '../../clients/forms/search';
import SectionTabs from '../../navigation/section-tabs';
import { PLANNING_TABS } from '../../navigation/tabs';
import { useOpuPlan } from '../contexts/opu-plan';

import './styles/opu-planner.css';

// Planificador de OPU (decisión 0085 · Q-251). Previsión Bayesiana por donadora (μ±σ,
// encoge al hato) + preselección dura (3 reglas) + selección chance-constrained con
// garantía γ. Dos modos: cobertura (meta = embriones) · receptoras (meta = N, 1:1).
// El backend calcula todo; el front arma la consulta y muestra el plan (recomendadas +
// flaggeadas con motivo) y el bloque de cobertura (garantía, prob. de llenado, excedente).

const REASON = {
    recently_aspirated: 'OPU reciente (<21 d)',
    recently_serviced:  'Servida reciente (<21 d)',
    pregnant_over_90d:  'Preñez >90 d',
    not_selected:       'No requerida (garantía cubierta)',
};
const reasonLabel = (r) => String(r || '').split(',').map((x) => REASON[x.trim()] || x.trim()).join(' · ');

const n1 = (v) => (v == null ? '—' : Number(v).toFixed(1));
const n2 = (v) => (v == null ? '—' : Number(v).toFixed(2));
const pct01 = (v) => (v == null ? '—' : `${Math.round(Number(v) * 100)}%`);

function DonorName({ d }) {
    return (
        <Link to={`/admin/animals/${d.animal_id}`} className="opuplan__donor-link" title="Ver ficha">
            {d.name || d.number || `#${d.animal_id}`}
            {d.number && d.name && <span className="opuplan__donor-num"> · {d.number}</span>}
        </Link>
    );
}

export default function OpuPlanner() {
    const [mode, setMode] = useState('coverage');       // 'coverage' | 'recipients'
    const [target, setTarget] = useState('30');         // embriones (cobertura)
    const [recipients, setRecipients] = useState('20'); // N receptoras
    const [gamma, setGamma] = useState(0.8);            // confianza de la garantía
    const [farm, setFarm] = useState(null);             // { id, name } | null
    const [committed, setCommitted] = useState(null);   // params que disparan la consulta

    const { data, isFetching, error } = useOpuPlan(committed);

    const plan = () => {
        const p = { mode, gamma };
        if (farm?.id) p.farm_id = farm.id;
        if (mode === 'coverage') {
            if (!(Number(target) >= 1)) { toast.error('Ingresá la meta de embriones (≥1)'); return; }
            p.target = Number(target);
        } else {
            if (!(Number(recipients) >= 1)) { toast.error('Ingresá la cantidad de receptoras (≥1)'); return; }
            p.recipients = Number(recipients);
        }
        setCommitted(p);
    };

    const pickFarm = (item, type) => { if (type === 'farm') setFarm({ id: item.id, name: item.name, owner: item.owner?.name }); };

    const cov = data?.coverage;
    const recommended = data?.plan?.recommended || [];
    const flagged = data?.plan?.flagged || [];
    const herd = data?.herd;

    // Agrupa flaggeadas por motivo (para leerlas ordenadas).
    const flaggedGroups = useMemo(() => {
        const map = new Map();
        for (const f of (data?.plan?.flagged || [])) {
            const key = f.reason || 'not_selected';
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(f);
        }
        // inelegibles primero, "no requerida" al final.
        return [...map.entries()].sort((a, b) => (a[0] === 'not_selected' ? 1 : 0) - (b[0] === 'not_selected' ? 1 : 0));
    }, [data]);

    return (
        <div className="opuplan">
            <SectionTabs tabs={PLANNING_TABS} />

            <header className="opuplan__header">
                <h1 className="opuplan__title">Planear OPU</h1>
                <p className="opuplan__subtitle">
                    Previsión Bayesiana por donadora con garantía de confianza. Elegí una meta y el motor selecciona el
                    menor grupo de donadoras que la cumple.
                </p>
            </header>

            {/* ── controles ── */}
            <section className="opuplan__controls">
                <div className="opuplan__seg" role="tablist" aria-label="Modo de planeación">
                    <button type="button" role="tab" aria-selected={mode === 'coverage'} className={`opuplan__seg-btn ${mode === 'coverage' ? 'opuplan__seg-btn--active' : ''}`} onClick={() => setMode('coverage')}>Por cobertura</button>
                    <button type="button" role="tab" aria-selected={mode === 'recipients'} className={`opuplan__seg-btn ${mode === 'recipients' ? 'opuplan__seg-btn--active' : ''}`} onClick={() => setMode('recipients')}>Por receptoras</button>
                </div>

                <div className="opuplan__fields">
                    {mode === 'coverage' ? (
                        <label className="opuplan__field">
                            <span className="opuplan__label">Meta de embriones</span>
                            <input type="number" min="1" step="1" value={target} onChange={(e) => setTarget(e.target.value)} />
                        </label>
                    ) : (
                        <label className="opuplan__field">
                            <span className="opuplan__label">Receptoras a llenar</span>
                            <input type="number" min="1" step="1" value={recipients} onChange={(e) => setRecipients(e.target.value)} />
                        </label>
                    )}

                    <label className="opuplan__field opuplan__field--gamma">
                        <span className="opuplan__label">Confianza (γ) · <strong>{pct01(gamma)}</strong></span>
                        <input type="range" min="0.5" max="0.99" step="0.01" value={gamma} onChange={(e) => setGamma(Number(e.target.value))} />
                    </label>

                    <div className="opuplan__field opuplan__field--farm">
                        <span className="opuplan__label">Finca (opcional)</span>
                        {farm ? (
                            <div className="opuplan__farm">
                                <span className="opuplan__farm-name">{farm.name}{farm.owner ? ` · ${farm.owner}` : ''}</span>
                                <button type="button" className="opuplan__farm-clear" onClick={() => setFarm(null)} aria-label="Quitar finca"><IconClose /></button>
                            </div>
                        ) : (
                            <ClientFarmSearch scope="all" farmsOnly placeholder="Todas las fincas…" onSelect={pickFarm} />
                        )}
                    </div>

                    <button type="button" className="opuplan__go" onClick={plan} disabled={isFetching}>
                        {isFetching ? 'Calculando…' : 'Planear'}
                    </button>
                </div>
            </section>

            {error && (
                <div className="opuplan__error">
                    {error.status === 403 ? 'No tenés permiso para el planificador.' : (error.status === 400 ? (error.message || 'Parámetros inválidos.') : (error.message || 'No se pudo calcular el plan.'))}
                </div>
            )}

            {!committed ? (
                <p className="opuplan__hint">Elegí el modo y la meta, luego “Planear”.</p>
            ) : data ? (
                <>
                    {/* ── bloque de cobertura / garantía ── */}
                    <section className={`opuplan__coverage ${cov?.met ? '' : 'opuplan__coverage--unmet'}`}>
                        <div className="opuplan__cov-lead">
                            <span className="opuplan__cov-count">{cov?.donors_selected ?? 0}</span>
                            <span className="opuplan__cov-count-label">donadora{cov?.donors_selected === 1 ? '' : 's'} recomendada{cov?.donors_selected === 1 ? '' : 's'}</span>
                            <span className={`opuplan__cov-chip ${cov?.met ? 'opuplan__cov-chip--ok' : 'opuplan__cov-chip--warn'}`}>
                                {cov?.met ? 'Cumple la garantía' : 'No alcanza la meta'}
                            </span>
                        </div>
                        <div className="opuplan__cov-grid">
                            <div className="opuplan__cov-stat">
                                <span className="opuplan__cov-val">{n1(cov?.expected)}</span>
                                <span className="opuplan__cov-lbl">Embriones esperados (Σμ)</span>
                            </div>
                            <div className="opuplan__cov-stat opuplan__cov-stat--accent">
                                <span className="opuplan__cov-val">{n1(cov?.lower_bound)}</span>
                                <span className="opuplan__cov-lbl">Garantía al {pct01(gamma)} (Σμ − z·σ)</span>
                            </div>
                            <div className="opuplan__cov-stat">
                                <span className="opuplan__cov-val">{pct01(cov?.fill_probability)}</span>
                                <span className="opuplan__cov-lbl">Prob. de llenar la meta</span>
                            </div>
                            <div className="opuplan__cov-stat">
                                <span className="opuplan__cov-val">{n1(cov?.surplus)}</span>
                                <span className="opuplan__cov-lbl">Excedente esperado</span>
                            </div>
                            <div className="opuplan__cov-stat">
                                <span className="opuplan__cov-val">{cov?.target ?? '—'}</span>
                                <span className="opuplan__cov-lbl">{mode === 'recipients' ? 'Receptoras (meta)' : 'Embriones (meta)'}</span>
                            </div>
                        </div>
                    </section>

                    {/* ── recomendadas ── */}
                    <section className="opuplan__section">
                        <h2 className="opuplan__section-title">Donadoras recomendadas <span className="opuplan__section-count">{recommended.length}</span></h2>
                        {!recommended.length ? (
                            <p className="opuplan__empty">No hay donadoras elegibles para esta meta{farm ? ' en esta finca' : ''}.</p>
                        ) : (
                            <div className="opuplan__table-wrap">
                                <table className="opuplan__table">
                                    <thead>
                                        <tr>
                                            <th className="opuplan__th-left">Donadora</th>
                                            <th>Embriones (μ ± σ)</th>
                                            <th>Conversión (p̂)</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {recommended.map((d) => (
                                            <tr key={d.animal_id}>
                                                <td className="opuplan__th-left"><DonorName d={d} /></td>
                                                <td className="opuplan__mono"><strong>{n1(d.mu)}</strong> ± {n1(d.sigma)}</td>
                                                <td className="opuplan__mono">{pct01(d.p_hat)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </section>

                    {/* ── flaggeadas (colapsable) ── */}
                    {flagged.length > 0 && (
                        <details className="opuplan__flagged">
                            <summary className="opuplan__flagged-head">
                                <span>Donadoras descartadas</span>
                                <span className="opuplan__section-count">{flagged.length}</span>
                            </summary>
                            <div className="opuplan__flagged-body">
                                {flaggedGroups.map(([reason, list]) => (
                                    <div key={reason} className="opuplan__flag-group">
                                        <span className={`opuplan__flag-reason ${reason === 'not_selected' ? 'opuplan__flag-reason--soft' : 'opuplan__flag-reason--hard'}`}>{reasonLabel(reason)} · {list.length}</span>
                                        <ul className="opuplan__flag-list">
                                            {list.map((d) => (
                                                <li key={d.animal_id} className="opuplan__flag-item">
                                                    <DonorName d={d} />
                                                    <span className="opuplan__flag-mu">μ {n2(d.mu)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </details>
                    )}

                    {/* ── contexto del hato ── */}
                    {herd && (
                        <p className="opuplan__herd">
                            Hato: {herd.donors_with_history} donadora(s) con historia · conversión media {pct01(herd.conversion_mean)} · viables medios {n1(herd.viables_mean)}
                            {data.recipients_available != null && <> · receptoras disponibles {data.recipients_available}</>}
                        </p>
                    )}
                </>
            ) : (
                <p className="opuplan__hint">Calculando…</p>
            )}
        </div>
    );
}
